import type { ZodType, ZodTypeDef } from 'zod';

export function getJSONFromLocalStorage<T = unknown>(key: string): T | null {
  const item = localStorage.getItem(key);
  if (!item) return null;

  try {
    return JSON.parse(item);
  } catch (_e) {
    localStorage.removeItem(key);
  }

  return null;
}

// The input type param is `unknown` (not `T`) so `T` infers from the schema's
// OUTPUT — required for `.catch(...)` schemas, whose input differs from output.
export function getValidatedFromLocalStorage<T>(key: string, schema: ZodType<T, ZodTypeDef, unknown>): T | null {
  const raw = getJSONFromLocalStorage(key);
  if (raw === null) return null;

  const result = schema.safeParse(raw);
  if (result.success) return result.data;

  localStorage.removeItem(key);
  return null;
}

/** Persist a JSON-serializable value; a full/unavailable store must never throw. */
export function setJSONInLocalStorage(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (_e) {
    // Storage full or unavailable (e.g. private mode) must not break the caller.
  }
}

// ── sessionStorage variants ──────────────────────────────────────────────────
// Same contract as the localStorage helpers above, against per-TAB storage that
// survives reload + in-tab navigation but dies with the tab and never leaks to
// another tab. Merely TOUCHING `sessionStorage` throws when it is disabled
// (private mode, blocked cookies) or absent (SSR), so every access is guarded —
// an unavailable store degrades to "no value" / a silent no-op, never a throw.

function getJSONFromSessionStorage<T = unknown>(key: string): T | null {
  try {
    const item = sessionStorage.getItem(key);
    if (!item) return null;
    return JSON.parse(item) as T;
  } catch (_e) {
    // Malformed blob or an unavailable store both degrade to "no value". Drop a
    // malformed entry when we can still reach the store; ignore if we cannot.
    try {
      sessionStorage.removeItem(key);
    } catch (_removeError) {
      // Store unreachable — nothing to clean up.
    }
    return null;
  }
}

/**
 * Read + schema-validate a sessionStorage value in one step — the session twin of
 * {@link getValidatedFromLocalStorage}. A malformed OR schema-mismatched blob is
 * dropped and treated as absent, so a stale/tampered entry degrades to "no value".
 */
export function getValidatedFromSessionStorage<T>(key: string, schema: ZodType<T, ZodTypeDef, unknown>): T | null {
  const raw = getJSONFromSessionStorage(key);
  if (raw === null) return null;

  const result = schema.safeParse(raw);
  if (result.success) return result.data;

  try {
    sessionStorage.removeItem(key);
  } catch (_e) {
    // Store unreachable — nothing to clean up.
  }
  return null;
}

/** Persist a JSON-serializable value to the tab's sessionStorage; a full/unavailable store must never throw. */
export function setJSONInSessionStorage(key: string, value: unknown): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch (_e) {
    // Storage full or unavailable (e.g. private mode) must not break the caller.
  }
}

/** Remove a tab-scoped value; an unavailable store is a silent no-op. */
export function removeFromSessionStorage(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch (_e) {
    // Store unreachable — nothing to remove.
  }
}
